export const Tree: NodeT = {
    id: '',
    name: '',
    icon: '',
    input: [],
    output: {},
    description: '',
    kind: 'Action',
    path: []
}

export const TriggerTree: NodeT = {
    id: '',
    name: '',
    icon: '',
    input: [],
    output: {},
    description: '',
    kind: 'Action',
    path: []
}

export const EndNode: NodeT = {
    id: '',
    name: '',
    icon: '',
    input: [],
    output: {},
    description: '',
    kind: 'End',
    path: []
}

export enum NodeMode {
    View,
    Add,
    Edit,
    Loading,
    Error,
    Delete
}

export type NodeViewT = {
    mode: NodeMode.View
}

export type NodeAddT = {
    mode: NodeMode.Add
}

export type NodeEditT = {
    mode: NodeMode.Edit
    nodeEditInput: NodeT
    nodeOutputConfig: ActionInputsListT[]
}

export type NodeDeleteT = {
    mode: NodeMode.Delete
    nodeEditInput: NodeT
    loading: boolean
    parentNode: NodeT
    error: {
        error: boolean
        info: string
    }
}

export type PopupLoadingT = {
    mode: NodeMode.Loading
}

export type PopupErrorT = {
    mode: NodeMode.Error
    error: string
}

export type NodeModes = NodeViewT | NodeAddT | NodeEditT | PopupLoadingT | PopupErrorT | NodeDeleteT

export const NodeView: NodeViewT = {
    mode: NodeMode.View
}

export const NodeAddT: NodeAddT = {
    mode: NodeMode.Add
}

export const NodeEdit: NodeEditT = {
    mode: NodeMode.Edit,
    nodeEditInput: Tree,
    nodeOutputConfig: []
}

export const NodeDelete: NodeDeleteT = {
    mode: NodeMode.Delete,
    nodeEditInput: Tree,
    loading: false,
    parentNode: Tree,
    error: {
        error: false,
        info: ''
    }
}

export const PopupLoading: PopupLoadingT = {
    mode: NodeMode.Loading
}

export const PopupError: PopupErrorT = {
    mode: NodeMode.Error,
    error: ''
}

export type ValueTypeT = { id: string; value: string; type: 'string' | 'context'; valuePath: string[]; icon: string }

export type ContextInputValueTypeT = { nodeInput: string; value: ValueTypeT[] }

export type RightPanelStateT = {
    show: boolean
    parentNode: TreeT<NodeT>
    mode: NodeModes
    currentNode:
        | ActionsResponseT
        | TriggerResponseT
        | ConditionResponseT
        | AppTriggerResponseT
        | EndResponseT
        | HowItWorksResponseT
}

export type LeftPanelStateT = {
    show: boolean
    step1: {
        done: boolean
    }
    step2: {
        done: boolean
    }
    step3: {
        done: boolean
    }
    parentNode: TreeT<NodeT>
    mode: NodeModes
    currentNode: ActionsResponseT | ConditionResponseT | AppTriggerResponseT | EndResponseT
}

export type RightPanelStateContext = {
    rightPanelState: RightPanelStateT
    setRightPanelState: (object: any) => void
    editTreeData: any
    setEditTreeData: (node: any) => void
    treeData: any
    disableDeployButton: boolean[]
    setDisableDeployButton: React.Dispatch<React.SetStateAction<boolean[]>>
    active_automation: boolean
}

export type LeftPanelStateContextT = {
    leftPanelState: LeftPanelStateT
    setLeftPanelState: (object: any) => void
}

export enum FlowInput {
    Name = 'flowName',
    Description = 'flowDescription'
}

export type SaveAs = {
    showSaveAsPopup: boolean
    flowName: string
    flowDetail: string
}

export type FlowDetailS = {
    id: string | null
    url: string
    toFlow: boolean
    saveAs: SaveAs
}

export const initialSaveAsData = {
    showSaveAsPopup: false,
    flowName: '',
    flowDetail: ''
}

type NodeType =
    | 'Action'
    | 'Approval'
    | 'AppTrigger'
    | 'EventTrigger'
    | 'Logic'
    | 'Function'
    | 'ForEach'
    | 'Condition'
    | 'Repeat'
    | 'NoTrigger'
    | 'End'
    | 'Setup'
    | 'ParallelPath'
    | 'ErrorHandlingCondition'
    | 'Timer'

export type NodeT = {
    id: string
    name: string
    icon: any
    input: InputFieldT[] | (InputRulesArray | hasCombinatorT)[]
    output: any
    description: string
    kind: NodeType
    meta_data?: any
    path: {
        actionId: string
        nodeId: string
        forEachId?: string
        forEachName?: string
        forEachNodeId?: string
        forEachSubflow?: {
            actionId: string
            nodeId: string
            forEachId?: string
            forEachName?: string
            forEachNodeId?: string
        }[]
    }[]
    subflow?: { children: TreeT<NodeT>[] }
}

export interface TreeT<T> {
    value: T
    children: Array<TreeT<T>>
}

export function checkInputHasValue(value: NodeT) {
    switch (value.kind) {
        case 'Action': {
            const input = value.input as InputFieldT[]
            const comparingInput = input.filter((val: InputFieldT) => val.required) as InputFieldT[]

            const filteredInput = input.filter(val => val.required && val.value && val.value.length > 0)

            return filteredInput.length === comparingInput.length
        }
        case 'Condition':
        case 'ErrorHandlingCondition': {
            const input = value.input as InputRulesArray[]

            const comparingInput = input.filter(
                (val: InputRulesArray) => val.type !== 'combinator'
            ) as InputRulesArray[]

            const filteredInput = input.filter(
                val =>
                    val.left &&
                    val.left.length > 0 &&
                    ((val.valType && val.valType === 'boolean') || (val.right && val.right.length > 0))
            )

            return filteredInput.length === comparingInput.length
        }

        default:
            return false
    }
}

export enum Nodes {
    HowItWorks,
    Trigger,
    NoTrigger,
    Setup,
    Action,
    Condition,
    End
}

export type ActionT = {
    kind: Nodes.Action
    Component: any
    SelectApplication: any
    Inputs: any
    ErrorHandling: any
    ActionInfo: any
}

export type ConditionT = {
    kind: Nodes.Condition
    Component: any
    SelectApplication: any
    Inputs: any
    ErrorHandling: any
    ActionInfo: any
}

export type CanvasRightPanelT = {
    Component: any
    ToggleArrow: any
    AppsDropDown: any
    StepHeader: any
    Step: TriggerT | ActionT | ConditionT
}

export const Action: ActionT = {
    kind: Nodes.Action,
    Component: null,
    SelectApplication: null,
    Inputs: null,
    ErrorHandling: null,
    ActionInfo: null
}

export enum AuthType {
    Apitoken = 'api_token',
    Basic = 'basic',
    Oauth = 'oauth',
    Custom = 'custom',
    None = ''
}
export type AuthenticationType = 'api_token' | 'basic' | 'oauth' | ''

export type ResponseOutputT = {
    action: string
    appId: string
    appName: string
    createdDate: string
    file: string
    authentication_type: AuthType
    id: string
    scope: string
    icon: string
    type: 'trigger' | 'action'
    version: string
    latest: boolean
}

export type AppsListT = {
    type: string
    output: ResponseOutputT[]
}

export type LookUpEnum = {
    kind: 'lookup-enum'
    lookup: string
    params: {
        [key: string]: string
    }
}

export type InputEnum = {
    kind: 'enum'
    values: {
        text: string
        value: any
    }[]
}

export type InputText = {
    kind: 'text'
}

export type InputPassword = {
    kind: 'password'
}

export type InputFieldT = {
    key: string
    displayTitle: string
    description: string
    required: boolean
    config: InputEnum | InputText | InputPassword | LookUpEnum
    value: string
    inputHasValue: boolean
    disabled?: boolean
}

export type ConfigPropertiesTypeT = 'object'

export type OutpuTypeT = 'string' | 'boolean' | 'date'

export type OutputListTypeT = {
    $id: string
    type: OutpuTypeT
    title: string
    examples: any[]
    value?: string
}

export type ObjectOutputListTypeT = {
    $id: string
    type: 'object'
    title: string
    properties: {
        [key: string]: OutputListTypeT
    }
}

export type PathIdType = {
    pathId: {
        actionId: string
        nodeId: string
    }
}

export type ActionInputsListT = {
    type: string
    output: {
        name: string
        interval?: string
        input: InputFieldT[]
        app?: ResponseOutputT & PathIdType
        output: {
            type: ConfigPropertiesTypeT
            title: string
            properties: {
                [key: string]: OutputListTypeT | ObjectOutputListTypeT
            }
        }
    }
}

export type ConnectionOutputT = {
    authStoreMap: string
    name: string
}

export enum ConnectionTypeT {
    old,
    new
}

export type ApitokenMeta = {
    api_token: string
}

export type OauthtokenMeta = {
    authStoreMap: string
}

export type BasicMeta = {
    username: string
    password: string
}

export type CustomAuthT = {
    type: AuthType.Custom
    customInputs: {
        id: string
        inputs: InputFieldT[]
    }
    value: {
        name: string
        type: AuthType
        appId: string
        workspace: string
        meta_data: any
    }
}

export type ApitokenT = {
    type: AuthType.Apitoken
    value: {
        name: string
        type: AuthType
        appId: string
        workspace: string
        meta_data: ApitokenMeta
    }
}

export type OauthtokenT = {
    type: AuthType.Oauth
    value: {
        name: string
        type: AuthType
        appId: string
        workspace: string
        meta_data: OauthtokenMeta
    }
}

export type BasicT = {
    type: AuthType.Basic
    value: {
        name: string
        type: AuthType
        appId: string
        workspace: string
        meta_data: BasicMeta
    }
}

export type NewConnectionTypeT = ApitokenT | BasicT | OauthtokenT | CustomAuthT

export type OldConnectionT = {
    type: ConnectionTypeT.old
    selectedConnection: string
    id: string
    valid: boolean | null
    hasError: string | null
    loading: boolean
    connectionsDetails: NewConnectionTypeT
}

export type NewConnectionT = {
    type: ConnectionTypeT.new
    id: string
    valid: boolean | null
    hasError: string | null
    loading: boolean
    selectedConnection: string
    connectionsDetails: NewConnectionTypeT
    error: { show: boolean; info: string }
    connectionEditing: boolean
}

export const ApiTokenValue: ApitokenT = {
    type: AuthType.Apitoken,
    value: {
        name: '',
        type: AuthType.Apitoken,
        appId: '',
        workspace: '',
        meta_data: {
            api_token: ''
        }
    }
}

export const OatuhTokenValue: OauthtokenT = {
    type: AuthType.Oauth,
    value: {
        name: '',
        type: AuthType.Oauth,
        appId: '',
        workspace: '',
        meta_data: {
            authStoreMap: ''
        }
    }
}

export const BasicValue: BasicT = {
    type: AuthType.Basic,
    value: {
        name: '',
        type: AuthType.Basic,
        appId: '',
        workspace: '',
        meta_data: {
            username: '',
            password: ''
        }
    }
}

export const CustomAuthValue: CustomAuthT = {
    type: AuthType.Custom,
    customInputs: { id: '', inputs: [] },
    value: {
        name: '',
        type: AuthType.Custom,
        appId: '',
        workspace: '',
        meta_data: {}
    }
}

export const NewConnection: NewConnectionT = {
    type: ConnectionTypeT.new,
    id: '',
    hasError: null,
    valid: null,
    loading: false,
    selectedConnection: 'Add a new Account',
    connectionsDetails: ApiTokenValue,
    error: { show: false, info: '' },
    connectionEditing: false
}

export const oldConnection: OldConnectionT = {
    type: ConnectionTypeT.old,
    id: '',
    valid: null,
    hasError: null,
    loading: false,
    selectedConnection: '',
    connectionsDetails: ApiTokenValue
}

export type SelectedConnectionT = OldConnectionT | NewConnectionT

export type ActionConnectionsT = {
    type: string
    output: ConnectionOutputT[]
}

export type ActionsResponseT = {
    nodeType: Nodes.Action
    appsList: AppsListT
    selectedApp: { name: string; id: string; type: AuthType; icon: string; version: string }
    selectedAction: { name: string; id: string; description: string }
    selectedConnection: SelectedConnectionT
    actionInputsList: ActionInputsListT
    connections: ActionConnectionsT
    errorHandling: {
        errorHandling: boolean
        errorHandlingParent: NodeT
        errors: { name: string; enabled: boolean }[]
        errorHandlingChildrenForAddMode: TreeT<NodeT>[]
    }
    parallelPathNode: {
        currentParallelPathNode: NodeT | null
        anotherEmptyChildren: NodeT | null
        emptyChildrenPosition: number
        replacingParallelSibling: NodeT | null
    }
}

export type HowItWorksResponseT = {
    nodeType: Nodes.HowItWorks
}

export type hasCombinatorT = {
    type: string
    combinator: string
}

export type RulesArray = {
    left: string
    operator: string
    right: string
    rightHasValue: boolean
    valType: string
    type: string
    hasCombinator: null | hasCombinatorT
}

export type InputRulesArray = {
    left: string
    operator: string
    right: string
    valType: string
    type: string
}

export type ConditionResponseT = {
    nodeType: Nodes.Condition
    appsList: AppsListT
    conditionName: string
    description: string
    rules: RulesArray[]
    input: (InputRulesArray | hasCombinatorT)[]
    parallelPathNode: {
        currentParallelPathNode: NodeT | null
        anotherEmptyChildren: NodeT | null
        emptyChildrenPosition: number
        replacingParallelSibling: NodeT | null
    }
}

export type TriggersResponseT = {
    nodeType: Nodes.Trigger
}

export enum Mode {
    View,
    Add,
    Edit,
    Delete,
    Search,
    Select,
    GroupView,
    Loading,
    Error,
    MoveToGroup,
    AciveOrDeactive,
    EditAutomationMeta,
    DeleteFlow,
    NoDataFound
}

export enum NonCanvasActionModes {
    None,
    Loading,
    NotFound,
    Error,
    Deploy,
    SaveAs,
    EditAutomationMeta,
    UploadToMarketPlace,
    Acknowledge
}

export type NonCanvasActions =
    | NoneT
    | LoadingT
    | NotFoundT
    | EditAutomationMetaT
    | ErrorT
    | DeployT
    | SaveAsT
    | AcknowledgeT
    | UploadToMarketPlaceT

export const Loading: LoadingT = {
    mode: NonCanvasActionModes.Loading
}

export const UploadToMarketPlace: UploadToMarketPlaceT = {
    mode: NonCanvasActionModes.UploadToMarketPlace,
    name: '',
    id: ''
}

export const DeployAcknowledgement: AcknowledgeT = {
    mode: NonCanvasActionModes.Acknowledge,
    message: '<b>Automation deployed</b>'
}
export const UploadToMarketPlaceAcknowledgement: AcknowledgeT = {
    mode: NonCanvasActionModes.Acknowledge,
    message: '<b>Automation uploaded to Marketplace</b>'
}
export const SaveAs: SaveAsT = {
    mode: NonCanvasActionModes.SaveAs,
    name: '',
    description: '',
    error: {
        error: false,
        info: ''
    }
}

export const ErrorPage: ErrorT = {
    mode: NonCanvasActionModes.Error
}

export const NotFoundPage: NotFoundT = {
    mode: NonCanvasActionModes.NotFound
}

export const Deploy: DeployT = {
    mode: NonCanvasActionModes.Deploy,
    error: {
        error: false,
        info: ''
    }
}

export const None: NoneT = {
    mode: NonCanvasActionModes.None
}
export const EditAutomationMeta: EditAutomationMetaT = {
    mode: NonCanvasActionModes.EditAutomationMeta,
    name: '',
    description: '',
    error: {
        error: false,
        info: ''
    }
}

export type NoneT = {
    mode: NonCanvasActionModes.None
}

export type ErrorT = {
    mode: NonCanvasActionModes.Error
}

export type LoadingT = {
    mode: NonCanvasActionModes.Loading
}

export type NotFoundT = {
    mode: NonCanvasActionModes.NotFound
}

export type UploadToMarketPlaceT = {
    mode: NonCanvasActionModes.UploadToMarketPlace
    id: string
    name: string
}

export type AcknowledgeT = {
    mode: NonCanvasActionModes.Acknowledge
    message: string
}

export type DeployT = {
    mode: NonCanvasActionModes.Deploy
    error: {
        error: boolean
        info: string
    }
}
export type SaveAsT = {
    mode: NonCanvasActionModes.SaveAs
    name: string
    description: string
    error: {
        error: boolean
        info: string
    }
}

export type EditAutomationMetaT = {
    mode: NonCanvasActionModes.EditAutomationMeta
    name: string
    description: string
    error: {
        error: boolean
        info: string
    }
}

export const ResponseType: string = 'success'

export const HowItWorksState: HowItWorksResponseT = {
    nodeType: Nodes.HowItWorks
}

export const ActionInitialState: ActionsResponseT = {
    nodeType: Nodes.Action,
    appsList: {
        type: ResponseType,
        output: []
    },
    selectedApp: { name: 'Select', id: '', type: AuthType.None, icon: '', version: '' },
    selectedAction: { name: 'Select', id: '', description: '' },
    selectedConnection: NewConnection,
    actionInputsList: {
        type: ResponseType,
        output: {
            name: '',
            input: [],
            output: {
                type: 'object',
                title: '',
                properties: {
                    sample: {
                        $id: '',
                        title: '',
                        type: 'string',
                        examples: []
                    }
                }
            }
        }
    },
    connections: {
        type: ResponseType,
        output: []
    },
    parallelPathNode: {
        currentParallelPathNode: null,
        anotherEmptyChildren: null,
        emptyChildrenPosition: 0,
        replacingParallelSibling: null
    },
    errorHandling: {
        errorHandling: false,
        errorHandlingParent: Tree,
        errors: [
            { name: 'success', enabled: false },
            { name: 'failed', enabled: false }
        ],
        errorHandlingChildrenForAddMode: []
    }
}

export const ConditionInitialState: ConditionResponseT = {
    nodeType: Nodes.Condition,
    appsList: {
        type: ResponseType,
        output: []
    },
    rules: [
        {
            left: '',
            operator: 'Select',
            right: '',
            rightHasValue: false,
            valType: '',
            type: 'operator',
            hasCombinator: null
        }
    ],
    input: [],
    conditionName: '',
    description: '',
    parallelPathNode: {
        currentParallelPathNode: null,
        anotherEmptyChildren: null,
        emptyChildrenPosition: 0,
        replacingParallelSibling: null
    }
}

export const LeftSidePanelDefaultValue = {
    show: true,
    step1: {
        done: false
    },
    step2: {
        done: false
    },
    step3: {
        done: false
    },
    mode: NodeView,
    parentNode: { value: Tree, children: [] },
    currentNode: ActionInitialState
}
// export type TreeNode<T> = Array<TreeT<T>>

export const DefaultSelectedValue: { name: string; value: any[] } = {
    name: 'Select',
    value: []
}

export const DefaultConditionValues: { [key: string]: { values: { name: string; value: any }[] } } = {
    number: {
        values: [
            {
                name: 'Equals',
                value: 'equals'
            },
            {
                name: 'Does not equal',
                value: 'not_equals'
            },
            {
                name: 'Greater Than',
                value: 'greater_than'
            },
            {
                name: 'Less Than',
                value: 'lesser_than'
            }
        ]
    },
    string: {
        values: [
            {
                name: 'Contains',
                value: 'contains'
            },
            {
                name: 'Doesn’t contain',
                value: 'not_contains'
            },
            {
                name: 'Starts with',
                value: 'starts_with'
            },
            {
                name: 'Doesn’t start with',
                value: 'not_starts_with'
            },
            {
                name: 'Ends with',
                value: 'ends_with'
            },
            {
                name: 'Doesn’t end with',
                value: 'not_ends_with'
            }
        ]
    },
    boolean: {
        values: [
            {
                name: 'Is True',
                value: 'is_true'
            },
            {
                name: 'Is False',
                value: 'is_false'
            }
        ]
    }
}

export const DefaultSelectedConnection: { name: string; value: any[] } = {
    name: 'Add a new Account',
    value: []
}

export const DefaultTree: TreeT<NodeT> = {
    value: {
        ...Tree,
        id: '',
        name: 'Triggered by Chatbot',
        kind: 'NoTrigger',
        output: { properties: {} },
        icon:
            'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDIzLjAuNCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkNhcGFfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiCgkgdmlld0JveD0iMCAwIDE2IDE2IiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCAxNiAxNjsiIHhtbDpzcGFjZT0icHJlc2VydmUiPgo8c3R5bGUgdHlwZT0idGV4dC9jc3MiPgoJLnN0MHtmaWxsOiMyREI2Nzc7fQo8L3N0eWxlPgo8cGF0aCBjbGFzcz0ic3QwIiBkPSJNMTMuODcsNi44MkwzLjQ1LDAuOEMyLjU3LDAuMywxLjQ4LDAuOTMsMS40OCwxLjkzdjEyLjAzYzAsMS4wMSwxLjA5LDEuNjQsMS45NywxLjE0bDEwLjQyLTYuMDIKCUMxNC43NCw4LjU4LDE0Ljc0LDcuMzIsMTMuODcsNi44MnoiLz4KPC9zdmc+Cg=='
    },
    children: [
        {
            value: {
                ...Tree,
                id: '',
                name: 'Step 2 - Setup',
                kind: 'Setup',
                icon:
                    'data:image/svg+xml;base64,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'
            },
            children: []
        },
        {
            value: {
                ...Tree,
                id: '',
                name: 'Step 3 - End',
                kind: 'End',
                meta_data: {
                    successEmail: '',
                    failureEmail: ''
                },
                icon:
                    'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDIzLjAuNCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkNhcGFfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiCgkgdmlld0JveD0iMCAwIDE2IDE2IiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCAxNiAxNjsiIHhtbDpzcGFjZT0icHJlc2VydmUiPgo8c3R5bGUgdHlwZT0idGV4dC9jc3MiPgoJLnN0MHtmaWxsOiNFNTRCMUE7fQo8L3N0eWxlPgo8cGF0aCBjbGFzcz0ic3QwIiBkPSJNMTIuODUsMTQuOEgzLjIxYy0xLjE0LDAtMi4wNi0wLjkyLTIuMDYtMi4wNlYzLjFjMC0xLjE0LDAuOTItMi4wNiwyLjA2LTIuMDZoOS42NGMxLjE0LDAsMi4wNiwwLjkyLDIuMDYsMi4wNgoJdjkuNjRDMTQuOTEsMTMuODgsMTMuOTksMTQuOCwxMi44NSwxNC44eiIvPgo8L3N2Zz4K'
            },
            children: []
        }
    ]
}

// Trigger
export type KeyDataType = 'Number' | 'Boolean' | 'String' | 'Date'

export type InputKey = {
    title: string
    uniqueKey: string
    type: KeyDataType
}
export type TriggerT = {
    triggerMeta: AppTriggerT | NoTriggerT
    outputSchema?: ObjectOutputListTypeT
}
export type AppTriggerT = {
    triggerType: 'AppTrigger'
}
export type NoTriggerT = {
    triggerType: 'NoTrigger'
    inputKeys: InputKey[]
}

export type TriggerResponseT = {
    nodeType: Nodes.Trigger
    trigger: TriggerT
}

export type AppTriggerResponseT = {
    nodeType: Nodes.Trigger
    trigger: TriggerT
    appsList: AppsListT
    selectedApp: { name: string; id: string; type: AuthType; icon: string; version: string }
    selectedAction: { name: string; id: string; description: string }
    interval?: string
    selectedConnection: SelectedConnectionT
    actionInputsList: ActionInputsListT
    connections: ActionConnectionsT
}

export const NoTriggerInitialState: TriggerResponseT = {
    nodeType: Nodes.Trigger,
    trigger: {
        triggerMeta: {
            triggerType: 'NoTrigger',
            inputKeys: []
        },
        outputSchema: undefined
    }
}

export const AppTriggerInitialState: AppTriggerResponseT = {
    nodeType: Nodes.Trigger,
    trigger: {
        triggerMeta: {
            triggerType: 'AppTrigger'
        }
    },
    appsList: {
        type: ResponseType,
        output: []
    },
    selectedApp: { name: 'Select', id: '', type: AuthType.None, icon: '', version: '' },
    selectedAction: { name: 'Select', id: '', description: '' },
    selectedConnection: NewConnection,
    actionInputsList: {
        type: ResponseType,
        output: {
            name: '',
            input: [],
            output: {
                type: 'object',
                title: '',
                properties: {
                    sample: {
                        $id: '',
                        title: '',
                        type: 'string',
                        examples: []
                    }
                }
            }
        }
    },
    connections: {
        type: ResponseType,
        output: []
    }
}

// End Node
export type EndResponseT = {
    nodeType: Nodes.End
    reportMeta: {
        successEmail: {
            valid: boolean
            value: string
        }
        failureEmail: {
            valid: boolean
            value: string
        }
    }
}

export const EndResponseInitialState: EndResponseT = {
    nodeType: Nodes.End,
    reportMeta: {
        successEmail: {
            valid: false,
            value: ''
        },
        failureEmail: {
            valid: false,
            value: ''
        }
    }
}

// run time logs types

export type outputConfig = {
    type: string
    output: {
        [key: string]: {
            $id: string
            type: string
            title: string
            value?: string
        }
    }
}

export type ActionNodeMeta = {
    action: {
        id: string
        auth_type: AuthType
        icon: string
        name: string
        description: string
    }
    authentication: AuthenticationT
}

export type ConditionNodeMeta = {
    name: string
}

export enum NodeTypeT {
    action = 'action',
    web_hook = 'web_hook',
    end = 'end',
    polling = 'polling',
    condition = 'condition'
}

export type executionStatus = 'failed' | 'success' | 'skipped'

export type PollingNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.polling
    node_execution_status: executionStatus
    input_schema: any
    output_schema: any
    node_meta: null
    inserted_date: string
}

export type WebHookNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.web_hook
    node_execution_status: executionStatus
    input_schema: any
    output_schema: any
    node_meta: null
    inserted_date: string
}

export type EndNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.end
    node_execution_status: executionStatus
    input_schema: any
    output_schema: any
    node_meta: {
        failureEmail: string
        successEmail: string
    }
    inserted_date: string
}

export type AuthenticationT =
    | {
          api_token: string
      }
    | {
          access_toke: string
      }
    | {
          username: string
          password: string
      }

export type ActionNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.action
    node_execution_status: executionStatus
    input_schema: {
        [key: string]: string
    }
    output_schema: any
    node_meta: ActionNodeMeta
    inserted_date: string
}

export type ConditionNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.condition
    node_execution_status: executionStatus
    input_schema: {
        [key: string]: string
    }
    output_schema: any
    node_meta: ConditionNodeMeta
    inserted_date: string
}

export type NormalViewActionNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.action
    node_execution_status: executionStatus
    input_schema: InputSchema[]
    output_schema: any
    node_meta: ActionNodeMeta
    inserted_date: string
}

export type InputSchema = {
    key: string
    displayTitle: string
    description: string
    required: boolean
    config: {
        kind: 'text' | 'enum'
    }
    value: string
}

export type LogsT = {
    type: string
    output: {
        normalView: (NormalViewActionNodeLogT | WebHookNodeLogT | EndNodeLogT | PollingNodeLogT)[]
        developerView: (ActionNodeLogT | WebHookNodeLogT | EndNodeLogT | PollingNodeLogT)[]
    }
}
export type ReactSetStateObject<T> = [T, React.Dispatch<React.SetStateAction<T>>]

export type AutomationMeta = {
    name: string
    description: string
    active_status: boolean
    deployed_status: string
    type: 'trigger' | 'no_trigger' | ''
    nodesWithoutConnection: string[]
    isLinkedToDialog: boolean
}
